import React, { useState } from 'react'
import moment from 'moment'
import { Pagination } from '../../components'
import { css } from '@emotion/core'
import { Formik, FormikProps } from 'formik'
import { API } from '../../utils'
import { Button, Checkbox, colors, Spinner, Table, Typography } from '@dw/helix'
import styled from '@emotion/styled'
import { PurchaseOrder } from './GenerateSerialNumbersPage'
import downloadFile from '../../utils/downloadFile'

import { checkboxStyles, columnCheckboxStyles, linkStyles, tableStyles, withEllipsisStyles } from './styles'

const buttonStyles = css`
  margin-left: 35px;
`

const ActionBlock = styled.div<any>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  color: ${(p: any) => p.disabled && `${colors.grey1}`};
`

interface DownloadPOFormProps {
  showPOSerialsInfo: (item: PurchaseOrder) => void
  searchQuery: string
  purchaseOrders: PurchaseOrder[]
  requestIsProcessing: boolean
  setRequestIsProcessing: (value: boolean) => void
}

interface Form {
  [key: string]: any
}

const DownloadPOForm: React.FunctionComponent<DownloadPOFormProps> = ({
  showPOSerialsInfo,
  purchaseOrders,
  searchQuery,
  requestIsProcessing,
}) => {
  const [page, setPage] = useState(1)

  const filteredPurchaseOrders = purchaseOrders.filter(({ orderId, vendorName, serialnumberStatus }: any) => {
    // show only new sorted by search query items
    return (
      serialnumberStatus === 'PROCESSED' &&
      ((orderId && orderId.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (vendorName && vendorName.toLowerCase().includes(searchQuery.toLowerCase())))
    )
  })

  const currentPageItems: PurchaseOrder[] = filteredPurchaseOrders.filter(
    (item, index) => index >= 20 * (page - 1) && index < 20 * page,
  )

  const onSubmit = async (values: Form) => {
    const purchaseOrderIds = Object.entries(values)
      .filter(entry => entry[1])
      .map(entry => entry[0])
      .join(',')

    const response = await API.get('its-excel-generator', `/supplier-serial`, {
      queryStringParameters: { purchaseOrderIds },
      responseType: 'text',
      response: true,
    })

    downloadFile(response.data, response.headers['content-disposition'].split('"')[1])
  }

  return (
    <Formik initialValues={{}} onSubmit={onSubmit}>
      {({ handleSubmit, setFieldValue, values }: FormikProps<Form>) => {
        const checked = Object.values(values).filter(order => order).length
        return (
          <form onSubmit={handleSubmit}>
            <Table
              sortable
              items={currentPageItems}
              columns={[
                {
                  label: (
                    <Checkbox
                      css={columnCheckboxStyles}
                      name="allCheckboxes"
                      checked={currentPageItems.filter(item => !values[item.orderId]).length === 0}
                      onChange={value => {
                        currentPageItems.forEach(item => {
                          setFieldValue(item.orderId, value)
                        })
                      }}
                    />
                  ),
                  render: item => (
                    <Checkbox
                      css={checkboxStyles}
                      name={`${item.orderId}`}
                      checked={values[item.orderId]}
                      onChange={value => {
                        setFieldValue(item.orderId, value)
                      }}
                    />
                  ),
                  width: '64px',
                },
                { label: 'PO Number', sortable: true, field: 'orderId', width: '106px' },
                {
                  label: 'Created Date',
                  sortable: true,
                  field: 'createdAt',
                  render: ({ createdAt }) => moment(createdAt).format('YYYY-MM-DD'),
                  width: '110px',
                },
                {
                  label: 'Supplier',
                  sortable: true,
                  field: 'vendorName',
                  render: ({ vendorName }) => (
                    <span title={vendorName} css={withEllipsisStyles}>
                      {vendorName}
                    </span>
                  ),
                  width: '110px',
                },
                {
                  width: '187px',
                  render: item => (
                    <Button theme="tertiary" css={linkStyles} onClick={() => showPOSerialsInfo(item)} type="button">
                      View serials
                    </Button>
                  ),
                },
              ]}
              rowKey={'orderId'}
              css={tableStyles}
            />
            {requestIsProcessing && (
              <Spinner
                color={colors.brandBlue}
                css={css`
                  padding: 10px;
                `}
              />
            )}
            <ActionBlock disabled={checked === 0}>
              <Typography
                css={css`
                  min-width: 182px;
                `}
              >
                {checked === 0 ? 'No' : `${checked}`} purchase order{checked === 1 ? '' : 's'} selected.
              </Typography>
              <Button css={buttonStyles} type="submit" disabled={checked === 0}>
                Download as excels
              </Button>
            </ActionBlock>
            {filteredPurchaseOrders.length > 0 && (
              <Pagination
                total={filteredPurchaseOrders.length}
                perPage={20}
                onChange={setPage}
                css={css`
                  margin-top: 25px;
                `}
              />
            )}
          </form>
        )
      }}
    </Formik>
  )
}

export default DownloadPOForm
